<template>
  <div class="w560 themev2">
    <div class="w300">
      <h1>Session Feedback</h1>
      <h3 v-if="!formNotFound">[{{sessionId}} - {{sessionName}}]</h3>
    </div>

    <div class="w300" v-if="formNotFound">
      <div class="formerror">Feedback form not found</div>
    </div>

    <div class="w300" v-if="errorMessage">
      <div class="formerror">{{ errorMessage }}</div>
      <br>
    </div>

    <ValidationObserver ref="feedbackForm" v-slot="{ invalid, handleSubmit }" tag="div" class="w300" v-if="!formNotFound">
      <form ref="form" @submit.prevent="handleSubmit(onSubmit)" :class="{ disabled: submitted }">
        
        <!-- Regular User Form -->
        <template v-if="!isOrgAdmin">
          <div class="inputrow">
            <label required>How would you rate the pedagogical value added by the {{ experienceName }} experience?</label>
            <div class="rating-group">
              <span class="emoji-sad">😔</span>
              <div class="radio-group">
                <label v-for="n in 5" :key="'rating1-' + n" class="radio-label">
                  <input v-model.number="form.pedagogicalValue" type="radio" :value="n" :disabled="submitted">
                  <span>{{ n }}</span>
                </label>
              </div>
              <span class="emoji-happy">😊</span>
            </div>
          </div>

          <div class="inputrow">
            <label required>How would you rate the learners' engagement in the session?</label>
            <div class="rating-group">
              <span class="emoji-sad">😔</span>
              <div class="radio-group">
                <label v-for="n in 5" :key="'rating2-' + n" class="radio-label">
                  <input v-model.number="form.engagementRating" type="radio" :value="n" :disabled="submitted">
                  <span>{{ n }}</span>
                </label>
              </div>
              <span class="emoji-happy">😊</span>
            </div>
          </div>

          <div class="inputrow">
            <label required>Did you encounter any technical issues during the session?</label>
            <div class="radio-group">
              <label class="radio-label">
                <input v-model="form.technicalIssues" type="radio" value="yes" :disabled="submitted">
                <span>Yes</span>
              </label>
              <label class="radio-label">
                <input v-model="form.technicalIssues" type="radio" value="no" :disabled="submitted">
                <span>No</span>
              </label>
            </div>
          </div>

          <div v-if="form.technicalIssues === 'yes'" class="inputrow">
            <label required for="technicalDetails">Please describe the problem in as much detail as possible:</label>
            <textarea
              v-model="form.technicalDetails"
              name="technicalDetails"
              :disabled="submitted"
              maxlength="5000"
              rows="6"
              placeholder="Describe the technical issue..."
            ></textarea>
            <div class="char-counter">{{ form.technicalDetails.length }}/5000</div>
          </div>

          <div class="inputrow">
            <label for="additionalFeedback">Please feel free to share any other feedback or suggestions you may have about the session and/or the experience.</label>
            <textarea
              v-model="form.additionalFeedback"
              name="additionalFeedback"
              :disabled="submitted"
              maxlength="5000"
              rows="6"
              placeholder="Share your feedback..."
            ></textarea>
            <div class="char-counter">{{ form.additionalFeedback.length }}/5000</div>
          </div>
        </template>

        <!-- OrgAdmin Form -->
        <template v-else>
          <div class="inputrow">
            <p>Based on our system records there were <strong>{{ activeLearners }}</strong> active learners in the session. If this number does not match the actual number of learners in the session, you may clarify it here.</p>
          </div>

          <div class="inputrow">
            <label for="actualLearners"><b>Actual number of learners</b> (if different from system record)</label>
            <input
              v-model.number="form.actualLearners"
              type="number"
              name="actualLearners"
              :disabled="submitted"
              min="0"
              placeholder="Enter actual number..."
            >
          </div>

          <div class="inputrow">
            <label for="learnersExplanation"><b>Additional details</b> (optional)<br>
              <small>If you have any context that might help explain the difference, please share it here.</small>
            </label>
            <textarea
              v-model="form.learnersExplanation"
              name="learnersExplanation"
              :disabled="submitted"
              maxlength="5000"
              rows="4"
              placeholder="Explain the number of learners..."
            ></textarea>
            <div class="char-counter">{{ form.learnersExplanation.length }}/5000</div>
          </div>

          <div class="inputrow">
            <label required>How would you rate the session preparation process?<br>
              <small>(This question refers to the processes of scheduling the session, assigning headsets and content transfers as well as classroom preparation)</small>
            </label>
            <div class="rating-group">
              <span class="emoji-sad">😔</span>
              <div class="radio-group">
                <label v-for="n in 5" :key="'prep-' + n" class="radio-label">
                  <input v-model.number="form.preparationRating" type="radio" :value="n" :disabled="submitted">
                  <span>{{ n }}</span>
                </label>
              </div>
              <span class="emoji-happy">😊</span>
            </div>
          </div>

          <div class="inputrow">
            <label required>How would you rate the technical performance during the live session?</label>
            <div class="rating-group">
              <span class="emoji-sad">😔</span>
              <div class="radio-group">
                <label v-for="n in 5" :key="'tech-' + n" class="radio-label">
                  <input v-model.number="form.liveSessionRating" type="radio" :value="n" :disabled="submitted">
                  <span>{{ n }}</span>
                </label>
              </div>
              <span class="emoji-happy">😊</span>
            </div>
          </div>

          <div class="inputrow">
            <label required>Did you encounter any technical issues during the session or while preparing it?</label>
            <div class="radio-group">
              <label class="radio-label">
                <input v-model="form.orgAdminTechnicalIssues" type="radio" value="yes" :disabled="submitted">
                <span>Yes</span>
              </label>
              <label class="radio-label">
                <input v-model="form.orgAdminTechnicalIssues" type="radio" value="no" :disabled="submitted">
                <span>No</span>
              </label>
            </div>
          </div>

          <div v-if="form.orgAdminTechnicalIssues === 'yes'" class="inputrow">
            <label required>At what stage did you encounter the issue?</label>
            <div class="checkbox-group">
              <label class="checkbox-label" v-for="stage in issueStages" :key="stage.key">
                <input
                  v-model="form.issueStages"
                  type="checkbox"
                  :value="stage.key"
                  :disabled="submitted"
                >
                <span>{{ stage.label }}</span>
              </label>
            </div>
          </div>

          <div v-if="form.orgAdminTechnicalIssues === 'yes'" class="inputrow">
            <label required>Were there specific devices that experienced errors or problems?</label>
            <div class="radio-group">
              <label class="radio-label">
                <input v-model="form.specificDevices" type="radio" value="yes" :disabled="submitted">
                <span>Yes</span>
              </label>
              <label class="radio-label">
                <input v-model="form.specificDevices" type="radio" value="no" :disabled="submitted">
                <span>No</span>
              </label>
            </div>
          </div>

          <div v-if="form.orgAdminTechnicalIssues === 'yes' && form.specificDevices === 'yes'" class="inputrow">
            <label required for="deviceNumbers">Please list the device numbers:</label>
            <textarea
              v-model="form.deviceNumbers"
              name="deviceNumbers"
              :disabled="submitted"
              maxlength="5000"
              rows="3"
              placeholder="List device numbers..."
            ></textarea>
            <div class="char-counter">{{ form.deviceNumbers.length }}/5000</div>
          </div>

          <div v-if="form.orgAdminTechnicalIssues === 'yes'" class="inputrow">
            <label required for="orgAdminTechnicalDetails">Please describe the problem in as much detail as possible:</label>
            <textarea
              v-model="form.orgAdminTechnicalDetails"
              name="orgAdminTechnicalDetails"
              :disabled="submitted"
              maxlength="5000"
              rows="6"
              placeholder="Describe the technical problem..."
            ></textarea>
            <div class="char-counter">{{ form.orgAdminTechnicalDetails.length }}/5000</div>
          </div>

          <div class="inputrow">
            <label for="orgAdminAdditionalFeedback">Please feel free to share any other feedback or suggestions you may have about the session.</label>
            <textarea
              v-model="form.orgAdminAdditionalFeedback"
              name="orgAdminAdditionalFeedback"
              :disabled="submitted"
              maxlength="5000"
              rows="6"
              placeholder="Share your feedback..."
            ></textarea>
            <div class="char-counter">{{ form.orgAdminAdditionalFeedback.length }}/5000</div>
          </div>
        </template>

        <br>
        <button class="insead" type="submit" :disabled="loading || submitted || !isFormValid()">
          {{ submitted ? 'Submitted!' : 'Submit' }}
        </button>

        <div class="formsuccess" v-if="submitted">
          Thank you for your valuable feedback!
        </div>

        <div class="formerror" v-if="submitError">{{ submitError }}</div>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import axios from 'axios'
import '@/veevalidate.js'

export default {
  name: 'Feedback',
  data: function() {
    return {
      form: {
        // Regular user fields
        pedagogicalValue: null,
        engagementRating: null,
        technicalIssues: '',
        technicalDetails: '',
        additionalFeedback: '',
        // OrgAdmin fields
        actualLearners: null,
        learnersExplanation: '',
        preparationRating: null,
        liveSessionRating: null,
        orgAdminTechnicalIssues: '',
        issueStages: [],
        specificDevices: '',
        deviceNumbers: '',
        orgAdminTechnicalDetails: '',
        orgAdminAdditionalFeedback: ''
      },
      submitted: false,
      errorMessage: undefined,
      submitError: undefined,
      loading: true,
      formNotFound: false,
      sessionId: this.$route.params.id,
      sessionName: '',
      activeLearners: 0,
      issueStages: [
        { key: 'preparation', label: 'Session device preparation (session scheduling, assigning devices, content transfer)' },
        { key: 'classroom', label: 'Class preparation (equipment setup in class, connectivity and wifi testing)' },
        { key: 'live', label: 'Live session (any issues that occurred during the actual session)' }
      ]
    }
  },
  computed: {
    isOrgAdmin() {
      return this.$store.getters.isOrgAdmin
    }
  },
  methods: {
    isFormValid() {
      if (this.isOrgAdmin) {
        return this.form.preparationRating !== null && 
               this.form.liveSessionRating !== null && 
               this.form.orgAdminTechnicalIssues !== ''
      } else {
        return this.form.pedagogicalValue !== null && 
               this.form.engagementRating !== null && 
               this.form.technicalIssues !== ''
      }
    },
    async onSubmit() {
      try {
        this.loading = true
        this.submitError = undefined
        
        let payload
        if (this.isOrgAdmin) {
          payload = {
            actualLearners: this.form.actualLearners,
            learnersExplanation: this.form.learnersExplanation || null,
            preparationRating: this.form.preparationRating,
            liveSessionRating: this.form.liveSessionRating,
            technicalIssues: this.form.orgAdminTechnicalIssues === 'yes',
            issueStages: this.form.issueStages,
            specificDevices: this.form.specificDevices === 'yes',
            deviceNumbers: this.form.deviceNumbers || null,
            technicalDetails: this.form.orgAdminTechnicalDetails || null,
            additionalFeedback: this.form.orgAdminAdditionalFeedback || null
          }
        } else {
          payload = {
            pedagogicalValue: this.form.pedagogicalValue,
            engagementRating: this.form.engagementRating,
            technicalIssues: this.form.technicalIssues === 'yes',
            technicalDetails: this.form.technicalDetails || null,
            additionalFeedback: this.form.additionalFeedback || null
          }
        }

        await axios.post(`sessions/${this.sessionId}/feedback`, payload)
        this.submitted = true
      } catch (err) {
        this.submitError = err.response?.data?.message || 'Failed to submit feedback. Please try again.'
      }
      finally{
        this.loading = false
      }
    }
  },
  components: {
    ValidationObserver
  },
  created: async function() {
    try {      
      const resp = await axios.get(`sessions/${this.sessionId}/feedback`)
      this.sessionName = resp.data?.sessionName || ''
      this.experienceName = resp.data?.experienceName || ''
      this.activeLearners = resp.data?.activeLearners || 0
      
      if (resp.data.feedback) {
        let tmp = JSON.parse(resp.data.feedback)
        
        if (this.isOrgAdmin) {
          // Map orgAdmin feedback data to form
          this.form.actualLearners = tmp.actualLearners
          this.form.learnersExplanation = tmp.learnersExplanation || ''
          this.form.preparationRating = tmp.preparationRating
          this.form.liveSessionRating = tmp.liveSessionRating
          this.form.orgAdminTechnicalIssues = tmp.technicalIssues ? 'yes' : 'no'
          this.form.issueStages = tmp.issueStages || []
          this.form.specificDevices = tmp.specificDevices ? 'yes' : 'no'
          this.form.deviceNumbers = tmp.deviceNumbers || ''
          this.form.orgAdminTechnicalDetails = tmp.technicalDetails || ''
          this.form.orgAdminAdditionalFeedback = tmp.additionalFeedback || ''
        } else {
          // Map regular user feedback data to form
          this.form.pedagogicalValue = tmp.pedagogicalValue
          this.form.engagementRating = tmp.engagementRating
          this.form.technicalIssues = tmp.technicalIssues ? 'yes' : 'no'
          this.form.technicalDetails = tmp.technicalDetails || ''
          this.form.additionalFeedback = tmp.additionalFeedback || ''
        }
        this.submitted = true
      }
    } catch (err) {
      if (err.response?.status === 404) {
        this.formNotFound = true
      } else {
        this.errorMessage = err.response?.data?.message || 'Failed to load feedback form.'
      }
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped lang="scss">
.rating-group {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  margin-bottom: 16px;
  clear: both;
}

.emoji-sad, .emoji-happy {
  font-size: 20px;
}

.radio-group {
  display: flex;
  gap: 16px;
  flex-wrap: nowrap;
  clear: both;
}

.checkbox-group {
  padding-top: 5px;
  margin-top: 12px;
  clear: both;
}

.checkbox-label, .radio-label {
  display: flex;
  gap: 8px;
  margin: 0;
  cursor: pointer;
  font-size: 14px;
  text-transform: none;
  font-weight: normal;
  margin-top: 0;
  margin-bottom: 0;

  &.radio-label{
    align-items: center;
  }
  &.checkbox-label{
    align-items: flex-start;
    margin-top: 5px;
    float: none;
  }

  input[type="checkbox"], input[type="checkbox"] {
    cursor: pointer;
    width: auto;
    height: auto;
    padding: 0;
    border: none;
    margin: 0;
    margin-top: 2px;
  }

  &:hover {
    text-decoration: underline;
  }
}

textarea {
  font-family: Roboto;
  resize: vertical;
}

.char-counter {
  font-size: 11px;
  color: #999;
  text-align: right;
  margin-top: 4px;
}

form.disabled {
  opacity: 0.6;
  pointer-events: none;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.inputrow {
  margin-bottom: 20px;
  span{
    color: unset;
  }
}
</style>
